/**
 * Shared page layout styles
 *
 * === SURFACE & CONTRAST HIERARCHY ===
 *
 * Page background (MainLayout `main`): colorNeutralBackground3 (#f5f5f5)
 * Cards / elevated surfaces:           colorNeutralBackground1 (#ffffff) — Fluent default
 * Nested tiles inside cards:           colorNeutralBackground2 (#fafafa)
 * Sidebar / outer layout:              colorNeutralBackground2 (#fafafa)
 *
 * Cards MUST visually stand out from the page. Never use the same
 * background token for both the page and card surfaces.
 *
 * === SPACING SCALE ===
 *
 * - XXXL (32px): gap between major page sections (cards, grids)
 * - XL  (20px): grid gaps between metric cards
 * - L   (16px): card body internal padding & gap between card sections
 * - S    (8px): gap between stacked text elements (label ↔ value)
 *
 * === MANDATORY RULES ===
 *
 * 1. Every flex container with stacked children MUST set an explicit `gap`.
 *    Never rely on implicit spacing between Typography components.
 * 2. Stat tiles / metric tiles must use flex-column + gap (spacingVerticalS)
 *    so labels and values never appear cramped.
 * 3. Card body areas use gap: spacingVerticalL (16px) between sub-sections
 *    (e.g. stat grid → progress bar row).
 * 4. Tile internal padding uses spacingVerticalL (16px) for breathing room.
 *
 * Usage: const styles = usePageStyles();
 *        <div className={styles.page}> ... </div>
 */
import { makeStyles, tokens } from '@fluentui/react-components';

export const usePageStyles = makeStyles({
  /** Page root — flex column with XXXXL gaps between sections */
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXXL,
  },

  /**
   * Optional full-height variant — same as `page` but also fills the available height
   * (`height: 100%; minHeight: 0`), for List & Grid Standard pages (`ListPageLayout`/
   * `ListFrame`) that need their root to participate in the height chain instead of
   * growing with content. Several migrated pages added a local `height: 100%` override
   * because `page` alone doesn't set one — apply this class instead of a bespoke one.
   * Combine with `page` via `mergeClasses(styles.page, styles.pageFill)` if the page-level
   * gap is also wanted; use standalone otherwise.
   */
  pageFill: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    gap: tokens.spacingVerticalXXXL,
  },

  /** Page title — adds breathing room below the heading */
  pageTitle: {
    marginBottom: 0, // gap on parent handles spacing to next sibling
  },

  /** Section heading row — title with optional action buttons */
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  /** Card body content area */
  cardBody: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  /** Metric card grid — responsive auto-fit with XL gaps */
  metricGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },

  /** Stat tile grid (inside cards, e.g. enforcement outcomes) */
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },

  /** Stat tile — centered metric with label */
  statTile: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    textAlign: 'center' as const,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },

  /** Metric value — monospace font for engineering precision */
  metricValue: {
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
    fontVariantNumeric: 'tabular-nums',
  },

  /** List of rows (e.g. recent runs) */
  rowList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  /** Individual row item */
  rowItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },

  /** Empty state message */
  emptyState: {
    color: tokens.colorNeutralForeground3,
    textAlign: 'center' as const,
    padding: tokens.spacingVerticalXXL,
  },

  /** Inline flex row with gap */
  inlineRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },

  /** Two-column grid (equal columns) */
  twoColumn: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },

  /** Three-column grid (2fr 1fr 1fr) */
  threeColumn: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr',
    gap: tokens.spacingHorizontalXL,
    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
  },
});

/**
 * Detail-drawer page-level styles.
 *
 * Use these when a page needs to customise content *inside* a DetailDrawer
 * beyond what the shared components provide. For the standard drawer chrome,
 * import { DetailDrawer, DetailSection, … } from 'components/common/DetailDrawer'.
 *
 * Usage: const dd = useDetailDrawerStyles();
 */
export const useDetailDrawerStyles = makeStyles({
  /** Flex-column content wrapper inside drawer body */
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },

  /** Badge row shown directly beneath the drawer title */
  headerBadges: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },

  /** Named section within the drawer */
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },

  /** Section title (semibold block text) */
  sectionTitle: {
    display: 'block',
  },

  /** 2-column metadata grid */
  metadataGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingVerticalS,
  },

  /** Stacked label → value pair */
  fieldStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },

  /** Caption-style metadata row */
  meta: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
});
